import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../../lib/supabase";
import {
  listQuestionsForDeal,
  urgencyForQuestion,
  type DispatchQuestion,
} from "./dispatchQuestionsApi";
import DispatchQuestionModal from "./DispatchQuestionModal";

/**
 * Tiny entry-point pill that goes on every dispatch deal card.
 *
 *  - Shows "💬 Ask" if no open questions
 *  - Shows "💬 N open" with color (slate <4hr, amber >4hr, red >24hr)
 *  - Shows "✓ N answered" if open questions = 0 but recent answers exist
 *
 * Tap → opens DispatchQuestionModal which handles all 3 modes.
 *
 * Loads its own state (lightweight count query). Subscribes to realtime so
 * answers/new questions update the pill instantly without a parent reload.
 */

interface DealLite {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  owner_id: string | null;
  split_rm_id: string | null;
  pre_inspection_assigned_to: string | null;
  phase: string | null;
}

interface Props {
  deal: DealLite;
  size?: "sm" | "md";
  /** Callback if you want the parent to know something changed (e.g. reload list views). */
  onChanged?: () => void;
}

export default function DispatchQuestionPill({ deal, size = "sm", onChanged }: Props) {
  const [questions, setQuestions] = useState<DispatchQuestion[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // No per-pill realtime subscription — having dozens of pills each open a
  // websocket channel was crashing the Schedule tab. The parent dispatch tab
  // already subscribes to `deals` realtime and triggers a reload that remounts
  // pills. Modal callbacks call onChanged which the parent uses to refresh.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = await listQuestionsForDeal(deal.id);
        if (!cancelled) setQuestions(qs);
      } catch {
        // Silently ignore — pill stays in default state.
      }
    })();
    return () => { cancelled = true; };
  }, [deal.id, reloadKey]);

  // What to display
  const open = questions.filter((q) => q.status === "open");
  const recentAnswered = questions.filter(
    (q) => q.status === "answered" && !q.acknowledged_by_asker_at,
  );
  const worstUrgency = open.reduce<"fresh" | "warning" | "overdue" | null>((acc, q) => {
    const u = urgencyForQuestion(q);
    if (u === "overdue") return "overdue";
    if (u === "warning" && acc !== "overdue") return "warning";
    if (acc === null) return "fresh";
    return acc;
  }, null);

  const hasOpen = open.length > 0;
  const hasUnacknowledgedAnswer = recentAnswered.length > 0;

  // Color classes
  const cls = (() => {
    if (hasOpen) {
      if (worstUrgency === "overdue") return "bg-red-100 text-red-800 border-red-300";
      if (worstUrgency === "warning") return "bg-amber-100 text-amber-800 border-amber-300";
      return "bg-blue-50 text-blue-700 border-blue-200";
    }
    if (hasUnacknowledgedAnswer) return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50";
  })();

  const label = (() => {
    if (hasOpen) return `💬 ${open.length} open`;
    if (hasUnacknowledgedAnswer) return `✓ ${recentAnswered.length} answered`;
    return "💬 Ask";
  })();

  const sizeCls = size === "md"
    ? "text-[12px] px-2 py-1"
    : "text-[10px] px-1.5 py-0.5";

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
        className={`inline-flex items-center font-semibold border rounded-md transition ${cls} ${sizeCls}`}
        title={hasOpen ? `${open.length} open question(s)` : hasUnacknowledgedAnswer ? `${recentAnswered.length} new answer(s)` : "Ask a question about this deal"}
      >
        {label}
      </button>

      {modalOpen && (
        <DispatchQuestionModal
          open
          deal={deal}
          onClose={() => setModalOpen(false)}
          onChanged={() => { reload(); onChanged?.(); }}
        />
      )}
    </>
  );
}

/** Convenience: load just the count without subscribing — for grids that already realtime-subscribe. */
export async function fetchQuestionCounts(dealIds: string[]): Promise<Record<string, { open: number; unacknowledged: number }>> {
  if (dealIds.length === 0) return {};
  const { data } = await supabase
    .from("dispatch_questions")
    .select("deal_id,status,acknowledged_by_asker_at")
    .in("deal_id", dealIds);
  const result: Record<string, { open: number; unacknowledged: number }> = {};
  (data ?? []).forEach((row: { deal_id: string; status: string; acknowledged_by_asker_at: string | null }) => {
    if (!result[row.deal_id]) result[row.deal_id] = { open: 0, unacknowledged: 0 };
    if (row.status === "open") result[row.deal_id].open += 1;
    if (row.status === "answered" && !row.acknowledged_by_asker_at) result[row.deal_id].unacknowledged += 1;
  });
  return result;
}
